import { Suspense, useEffect, useState } from "react";
import { Box, CircularProgress, Toolbar, Alert } from "@mui/material";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopHeader from "./TopHeader";
import { trackLoginOrReturnVisit } from "@/utils/productAnalytics";
import { useTenantProfile } from "@/hooks/useAssets";
// Trial restriction disabled for now (see App.tsx and
// TenantResolutionClaimsTransformation.cs) - banner would just show a
// countdown to a limit that no longer applies. Re-enable alongside those
// once trial enforcement is planned out properly.
// import TrialBanner from "./TrialBanner";

interface Props {
  mode: "light" | "dark";
  onToggleMode: () => void;
}

export default function AppLayout({ mode, onToggleMode }: Props) {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Cache hit, not a duplicate request - AuthenticatedApp (App.tsx) already
  // fetches this under the same ["tenant"] query key before this ever mounts.
  const { data: tenant } = useTenantProfile();

  // AppLayout only ever mounts once per authenticated session (it wraps the
  // whole app-shell route tree - see routes/index.tsx), so this fires
  // exactly once per session, not once per page navigation.
  useEffect(() => {
    trackLoginOrReturnVisit();
  }, []);

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <TopHeader mode={mode} onToggleMode={onToggleMode} onMenuClick={() => setMobileOpen((v) => !v)} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default"
        }}
      >
        <Toolbar />
        {tenant?.isDemo && (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            You're viewing a shared, read-only demo - changes won't be saved.{" "}
            <a href="/login?signup=1" style={{ fontWeight: 700 }}>Start your own free account</a> to try it for real.
          </Alert>
        )}
        {/* <TrialBanner /> */}
        {/* flex:1 + minHeight:0 lets pages with a data table (e.g. Assets) grow
            to fill the viewport instead of leaving empty gray space below a
            fixed-height table; pages that don't need to stretch are unaffected
            since a column flex item's height still follows its own content by default. */}
        <Box sx={{ p: { xs: 1.5, sm: 3 }, flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Suspense fallback={<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}
